// Pure logic behind the change page: what a supervisor needs to know, derived from the API's data.
import { ACTIVE_STATES, type Change, type ChangeState, type CiOverview, type Evidence, type Policy } from "../api/types";

const cache = new Map<string, RegExp>();

/** Same semantics as internal/lib/glob: `**` spans directories, `*` and `?` do not, a plain path matches its subtree. */
export function globToRegExp(g: string): RegExp {
  const hit = cache.get(g);
  if (hit) return hit;
  let s = "";
  for (let i = 0; i < g.length; ) {
    if (g.startsWith("**/", i)) {
      s += "(?:.*/)?";
      i += 3;
    } else if (g.startsWith("**", i)) {
      s += ".*";
      i += 2;
    } else if (g[i] === "*") {
      s += "[^/]*";
      i++;
    } else if (g[i] === "?") {
      s += "[^/]";
      i++;
    } else {
      s += g[i].replace(/[.+^${}()|[\]\\]/g, "\\$&");
      i++;
    }
  }
  if (!/[*?]/.test(g)) s += "(?:/.*)?";
  const re = new RegExp(`^${s}$`);
  cache.set(g, re);
  return re;
}

export const matchAny = (path: string, globs: string[] | undefined) => (globs ?? []).some((g) => globToRegExp(g).test(path));

export type TierWhy = {
  tier: "low" | "normal" | "high";
  /** The globs that decided the tier and the files each matched. */
  matches: Array<{ glob: string; paths: string[] }>;
  /** For normal: files that kept it from being low. */
  notLow: string[];
  protectedHits: string[];
  review: boolean;
};

/** Why policy gave these paths their tier, mirroring policy.Tier: any high match, else all low, else normal. */
export function explainTier(paths: string[], policy: Policy): TierWhy {
  const byGlob = (globs: string[] = []) =>
    globs.map((glob) => ({ glob, paths: paths.filter((p) => globToRegExp(glob).test(p)) })).filter((m) => m.paths.length > 0);
  const protectedHits = paths.filter((p) => matchAny(p, policy.protected));
  const high = byGlob(policy.tiers.high?.paths);
  const review = (t: TierWhy["tier"]) => !!policy.tiers[t]?.review;
  if (high.length) return { tier: "high", matches: high, notLow: [], protectedHits, review: review("high") };
  const lowGlobs = policy.tiers.low?.paths ?? [];
  const notLow = paths.filter((p) => !matchAny(p, lowGlobs));
  if (paths.length && !notLow.length) return { tier: "low", matches: byGlob(lowGlobs), notLow: [], protectedHits, review: review("low") };
  return { tier: "normal", matches: [], notLow, protectedHits, review: review("normal") };
}

const TRAILER = /^([A-Za-z][\w-]*): (.+)$/;

/** Splits a commit message into its body and its trailer block (`Key: value` lines at the end). */
export function splitMessage(message: string): { subject: string; body: string; trailers: Array<[string, string]> } {
  const [subject, ...rest] = message.split("\n");
  const paras = rest.join("\n").trim().split(/\n\s*\n/);
  const last = paras.at(-1) ?? "";
  const lines = last.split("\n").filter((l) => l.trim());
  if (paras.length && lines.length && lines.every((l) => TRAILER.test(l.trim()))) {
    return { subject, body: paras.slice(0, -1).join("\n\n"), trailers: lines.map((l) => TRAILER.exec(l.trim())!.slice(1, 3) as [string, string]) };
  }
  return { subject, body: paras.join("\n\n"), trailers: [] };
}

/** Code spans in the description that name files, and whether the change touches them. */
export function mentionedFiles(body: string, changed: string[]): Array<{ name: string; path: string | null }> {
  const seen = new Set<string>();
  const out: Array<{ name: string; path: string | null }> = [];
  for (const m of body.matchAll(/`([^`\s]+)`/g)) {
    const name = m[1];
    if (seen.has(name) || !/\.[A-Za-z0-9]{1,6}$/.test(name) || /^\d/.test(name)) continue;
    seen.add(name);
    const path = changed.find((p) => p === name || p.endsWith(`/${name}`)) ?? null;
    out.push({ name, path });
  }
  return out;
}

const RANK: Record<Evidence["status"], number> = { fail: 0, infra_error: 1, queued: 2, waiting: 3, pass: 4, skipped: 5 };
export const isRunning = (e: Evidence) => e.status === "queued" || e.status === "waiting";

/** The latest run only: the newest commit CI ran on, and the newest attempt of each job there. */
export function latestRun(evidence: Evidence[]): Evidence[] {
  if (!evidence.length) return [];
  const newest = evidence.reduce((a, e) => (e.queued_at > a.queued_at ? e : a), evidence[0]).commit_sha;
  const byJob = new Map<string, Evidence>();
  for (const e of evidence) {
    if (e.commit_sha !== newest) continue;
    const prev = byJob.get(e.job);
    if (!prev || e.attempt > prev.attempt) byJob.set(e.job, e);
  }
  return [...byJob.values()];
}

export type CheckGroup = { name: string; jobs: Evidence[]; status: Evidence["status"] };

/** Matrix instances collapse into their ci.yaml job; groups sort failures first, then running, then done. */
export function groupChecks(run: Evidence[]): CheckGroup[] {
  const groups = new Map<string, Evidence[]>();
  for (const e of run) {
    const k = e.job_group ?? e.job;
    groups.set(k, [...(groups.get(k) ?? []), e]);
  }
  return [...groups.entries()]
    .map(([name, jobs]) => {
      jobs.sort((a, b) => RANK[a.status] - RANK[b.status] || a.job.localeCompare(b.job));
      return { name, jobs, status: jobs[0].status };
    })
    .sort((a, b) => RANK[a.status] - RANK[b.status] || a.name.localeCompare(b.name));
}

/** Matrix values of an instance, e.g. "build-cli (goos=linux, goarch=amd64)" → "goos=linux, goarch=amd64". */
export const matrixLabel = (job: string) => /\((.*)\)$/.exec(job)?.[1] ?? job;

/**
 * Time until the run finishes: each unfinished job takes its historical average, after the
 * unfinished jobs it needs. Null when nothing is running or no job has history.
 */
export function estimateRemaining(run: Evidence[], totals: CiOverview["totals"], now = Date.now()): number | null {
  const avg = new Map(totals.filter((t) => t.runs > 0).map((t) => [t.job, t.ms / t.runs]));
  const byJob = new Map(run.map((e) => [e.job, e]));
  const memo = new Map<string, number>();
  let known = false;
  const finish = (job: string, depth = 0): number => {
    const cached = memo.get(job);
    if (cached !== undefined) return cached;
    const e = byJob.get(job);
    if (!e || !isRunning(e) || depth > 50) return 0;
    const a = avg.get(job);
    if (a !== undefined) known = true;
    const before = Math.max(0, ...(e.needs ?? []).map((n) => finish(n, depth + 1)));
    const own = a === undefined ? 0 : e.status === "queued" && before === 0 ? Math.max(0, a - (now - e.queued_at)) : a;
    memo.set(job, before + own);
    return before + own;
  };
  const left = Math.max(0, ...run.filter(isRunning).map((e) => finish(e.job)));
  return known && run.some(isRunning) ? left : null;
}

/** Who has to act next. */
export type Turn = "none" | "you" | "agent" | "admin" | "main" | "done";

export type Status = { turn: Turn; title: string; detail?: string };

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;

/** One sentence that answers "is this on track, and does it need me?". */
export function describeStatus(c: Change, run: Evidence[], o: { queuePosition?: number; etaMs?: number | null; canReview?: boolean } = {}): Status {
  const r = c.reason ?? {};
  const passed = run.filter((e) => e.status === "pass").length;
  const gating = run.filter((e) => e.status !== "skipped").length;
  const failed = run.filter((e) => e.status === "fail" || e.status === "infra_error").map((e) => e.job);
  const eta = o.etaMs ? `, about ${Math.max(1, Math.round(o.etaMs / 60_000))} min left` : "";
  switch (c.state) {
    case "created":
      return { turn: "none", title: "Opened. CI starts in a moment." };
    case "ci":
      if (r.code === "MAIN_RED")
        return {
          turn: "main",
          title: `Waiting for main to be fixed: ${(r.jobs ?? failed).join(", ")} also fail${(r.jobs ?? failed).length === 1 ? "s" : ""} on main.`,
          detail: "Not this change's fault. Bee retests it on main once main passes; nothing needed from you or the agent.",
        };
      if (r.code === "CHECKING_BASE")
        return { turn: "none", title: `A check failed (${failed.join(", ")}); Bee is checking whether main fails it too.`, detail: "If main fails it as well, the change waits for main instead of being rejected." };
      return { turn: "none", title: `Running CI: ${passed} of ${plural(gating, "check")} passed${eta}.` };
    case "review":
      return o.canReview
        ? { turn: "you", title: `CI passed. Its ${c.risk_tier} risk tier needs your approval to land.`, detail: "Read the files and approve or reject it in the sidebar." }
        : { turn: "you", title: `CI passed. Waiting for a person to approve this ${c.risk_tier} risk change.`, detail: "The agent cannot approve its own change." };
    case "ready":
      if (c.after) return { turn: "none", title: "Passed CI. Waiting for the change it is stacked on to land first." };
      return { turn: "none", title: `Passed CI. Queued to land${o.queuePosition ? `, ${ordinal(o.queuePosition)} in line` : ""}.` };
    case "train":
      return { turn: "none", title: "Testing on the exact commit that will land (merge train)." };
    case "landing":
      return { turn: "none", title: "Landing on main now." };
    case "landed":
      return { turn: "done", title: "Landed on main. Post-land verification is pending.", detail: "If verification fails, Bee bisects and may revert it." };
    case "verified":
      return { turn: "done", title: "Landed and verified on main." };
    case "conflict": {
      const files = (r.conflicts ?? []).map((f: { path: string }) => f.path);
      const by = (r.conflicts_with ?? []).map((w: { change_id: string }) => `#${w.change_id.slice(-8)}`);
      return {
        turn: "agent",
        title: `Conflict${files.length ? ` on ${files.join(", ")}` : ""}${by.length ? ` with ${by.join(", ")}` : ""}.`,
        detail: "The agent combines both edits and lands again; it keeps its place in the queue.",
      };
    }
    case "rejected":
      if (r.code === "CI_FAILED" || r.code === "TRAIN_FAILED") {
        const jobs = (r.failed ?? []).map((f: { job: string }) => f.job);
        return {
          turn: "agent",
          title: `${r.code === "TRAIN_FAILED" ? "Failed when combined with the changes ahead of it" : "CI failed"}${jobs.length ? `: ${jobs.join(", ")}` : ""}.`,
          detail: "The agent fixes the cause and lands again as a new attempt.",
        };
      }
      if (r.code === "REVIEW_REJECTED") return { turn: "agent", title: r.message ?? "A reviewer refused the change.", detail: "The agent addresses the comment in a new change." };
      if (["PROTECTED_PATH", "FORBIDDEN_PATH", "POLICY_DENIED"].includes(r.code))
        return { turn: "admin", title: `Not allowed: ${r.message ?? r.code}.`, detail: r.hint || "Only an admin can change policy or protected paths." };
      if (r.code === "PARENT_FAILED") return { turn: "agent", title: "The change it was stacked on failed, so this one was rejected too." };
      return { turn: "agent", title: r.message ?? "Rejected.", detail: r.hint };
    case "reverted": {
      const job = r.reverted?.failing?.[0]?.job;
      return { turn: "agent", title: `Reverted after landing${job ? `: ${job} failed on main` : ""}.`, detail: "Treat it as a new issue: the agent fixes it in a fresh change." };
    }
    case "cancelled":
      return { turn: "done", title: "Cancelled. Nothing landed." };
  }
  return { turn: "none", title: c.state };
}

/** Who acts next, from the change alone (no CI evidence): for lists. */
export function turnOf(c: Change): Turn {
  return describeStatus(c, []).turn;
}

/** Counts that answer "what needs me?" for a repo's open changes. */
export function turnCounts(open: Change[]) {
  let you = 0;
  let agent = 0;
  const working = new Set<string>();
  for (const c of open) {
    const t = turnOf(c);
    if (t === "you") you++;
    else if (t === "agent" || t === "admin") agent++;
    if (ACTIVE_STATES.includes(c.state)) working.add(c.agent_id);
  }
  return { you, agent, working: working.size };
}

/** Inbox sections for open changes: what needs a person, then everything agents and bee carry on their own. */
export function inboxSection(c: Change): "you" | "agents" {
  const t = turnOf(c);
  return t === "you" || t === "admin" ? "you" : "agents";
}

export const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th"}`;

/** Changes for the same task, oldest first: each re-land after a rejection is a new change. */
export function attemptsFor(c: Change, all: Change[]): Change[] {
  if (!c.task_id) return [c];
  const same = all.filter((x) => x.task_id === c.task_id && x.agent_id === c.agent_id);
  if (!same.some((x) => x.id === c.id)) same.push(c);
  return same.sort((a, b) => a.created - b.created);
}

/** States in which a change was not retried but done: under an intent, one task has many changes that each land. */
const DONE: ChangeState[] = ["landed", "verified"];

/** The newest later attempt at c's task by the same agent, if c has been retried (it did not land). */
export function laterAttempt(c: Change, all: Change[]): Change | undefined {
  if (DONE.includes(c.state)) return undefined;
  const tries = attemptsFor(c, all);
  const last = tries[tries.length - 1];
  return last.id === c.id ? undefined : last;
}

export type PathNode = { name: string; path: string; children: PathNode[]; file: boolean };

/** Paths as a tree with single-child directories folded together ("web/src/pages"). */
export function pathTree(paths: string[]): PathNode[] {
  const root: PathNode = { name: "", path: "", children: [], file: false };
  for (const p of [...paths].sort()) {
    let node = root;
    const parts = p.split("/");
    parts.forEach((part, i) => {
      let child = node.children.find((n) => n.name === part && n.file === (i === parts.length - 1));
      if (!child) {
        child = { name: part, path: parts.slice(0, i + 1).join("/"), children: [], file: i === parts.length - 1 };
        node.children.push(child);
      }
      node = child;
    });
  }
  const fold = (n: PathNode): PathNode => {
    let cur = n;
    while (!cur.file && cur.children.length === 1 && !cur.children[0].file) {
      const only = cur.children[0];
      cur = { ...only, name: cur.name ? `${cur.name}/${only.name}` : only.name };
    }
    return { ...cur, children: cur.children.map(fold) };
  };
  return root.children.map(fold);
}
